import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useGeneration, useUpdateGeneration } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import {
  VIDEO_RATIO_OPTIONS,
  VIDEO_RESOLUTION_OPTIONS,
  type VideoRatio,
  type VideoResolution,
} from '@/lib/constants'
import i18n from '@/i18n'
import {
  normalizeVideoDuration,
  normalizeVideoResolution,
} from '@/lib/video-settings'

// 어드민 UI는 한국어 고정 - 비율/해상도 라벨도 ko 리소스에서 직접 조회한다
const tKo = i18n.getFixedT('ko')

function isVideoRatio(value: string): value is VideoRatio {
  return VIDEO_RATIO_OPTIONS.some((option) => option.id === value)
}

export function GenerationForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: generation, isLoading } = useGeneration(id!)
  const updateMutation = useUpdateGeneration(id!)

  type GenerationFormData = {
    prompt: string
    ratio: VideoRatio
    resolution: VideoResolution
    duration: number
  }

  const baseFormData = useMemo<GenerationFormData>(
    () =>
      generation
        ? {
            prompt: generation.prompt || '',
            ratio: isVideoRatio(generation.ratio) ? generation.ratio : '16:9',
            resolution: normalizeVideoResolution(generation.resolution),
            duration: normalizeVideoDuration(generation.duration),
          }
        : {
            prompt: '',
            ratio: '16:9',
            resolution: '768P',
            duration: 5,
          },
    [generation],
  )
  const [draftFormData, setDraftFormData] = useState<GenerationFormData | null>(null)
  const formData = draftFormData ?? baseFormData

  const updateField = <K extends keyof GenerationFormData>(
    key: K,
    value: GenerationFormData[K],
  ) => {
    setDraftFormData((prev) => ({ ...(prev ?? baseFormData), [key]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.prompt.trim()) {
      toast.error('프롬프트를 입력하세요')
      return
    }

    try {
      const updateData = {
        prompt: formData.prompt,
        ratio: formData.ratio,
        resolution: formData.resolution,
        duration: formData.duration,
      }

      await updateMutation.mutateAsync(updateData)
      toast.success('생성 비디오가 수정되었습니다')
      navigate(`/admin/generations/${id}/view`)
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '수정 실패')
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-heading tracking-tight">생성 비디오 수정</h1>
        <p className="text-caption mt-2 leading-relaxed">생성된 비디오의 메타데이터를 수정합니다</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-lg border p-6">
        {/* Prompt */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            프롬프트 <span className="text-red-500">*</span>
          </label>
            <textarea
              value={formData.prompt}
              onChange={(e) => updateField('prompt', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
              rows={4}
              required
          />
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Ratio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">비율</label>
            <select
              value={formData.ratio}
              onChange={(e) => {
                const nextRatio = e.target.value
                if (isVideoRatio(nextRatio)) {
                  updateField('ratio', nextRatio)
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              {VIDEO_RATIO_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.id} - {tKo(option.labelKey)}
                </option>
              ))}
            </select>
          </div>

          {/* Resolution */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">해상도</label>
            <select
              value={formData.resolution}
              onChange={(e) => updateField('resolution', e.target.value as VideoResolution)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              {VIDEO_RESOLUTION_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.id} - {tKo(option.descriptionKey)}
                </option>
              ))}
            </select>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">길이 (초)</label>
            <Input
              type="number"
              value={formData.duration}
              onChange={(e) => updateField('duration', Number(e.target.value))}
              min={4}
              max={15}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3 pt-4">
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? '저장 중...' : '수정'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/admin/generations/${id}/view`)}
          >
            취소
          </Button>
        </div>
      </form>
    </div>
  )
}
